import { z } from 'zod'

// Проект. Read-модель со срезом смотрящего: employee-only поля (сумма,
// ответственный, команда, срок шага, ссылки) у заказчика отсутствуют —
// опциональны. Единственная дата карточки — срок текущего шага.

export const ProjectOutcomeSchema = z.object({
  result: z.enum(['won', 'lost']),
  reason_id: z.string().optional(),
  comment: z.string().optional(),
})
export type ProjectOutcome = z.infer<typeof ProjectOutcomeSchema>

export const StageEntrySchema = z.object({
  // Воронка этапа: карточка могла пройти его в другой воронке.
  board_id: z.string().default(''),
  column_id: z.string(),
  entered_at: z.string(),
  deadline: z.string().optional(),
})

export const ProjectLinkSchema = z.object({ title: z.string(), url: z.string() })
export type ProjectLink = z.infer<typeof ProjectLinkSchema>

// Карточка доски (employee-only): derived overdue приходит с бэка.
export const ProjectCardSchema = z.object({
  id: z.string(),
  company_id: z.string(),
  company_name: z.string(),
  company_slug: z.string(),
  slug: z.string(),
  title: z.string(),
  board_id: z.string().default(''),
  column_id: z.string(),
  // Срок текущего шага — единственная дата карточки.
  stage_deadline: z.string().optional(),
  outcome: ProjectOutcomeSchema.optional(),
  responsible_id: z.string().optional(),
  responsible_name: z.string().optional(),
  amount_kopecks: z.number().optional(),
  // Приоритет 1..10 (employee-only, бэк хранит nullable — отсутствие = не задан).
  priority: z.number().int().min(1).max(10).optional(),
  // Бэк опускает пустой набор тегов (omitempty) — коалесим в [] для плитки.
  tags: z.array(z.string()).default([]),
  overdue: z.boolean(),
  // Проект убран в архив: из доски он ушёл, показывается только с тумблером.
  archived: z.boolean().default(false),
  // Число открытых задач по проекту — показывается на плитке доски.
  open_task_count: z.number().optional(),
  // Открытые блокирующие задачи (бэк отдаёт на доске всегда).
  open_blocking_task_count: z.number().optional(),
})
export type ProjectCard = z.infer<typeof ProjectCardSchema>

// Сводка проекта (лента компании + контур заказчика /projects). Срез смотрящего:
// сумма/ответственный employee-only (опциональны).
export const ProjectSummarySchema = z.object({
  id: z.string(),
  company_id: z.string(),
  company_name: z.string(),
  company_slug: z.string(),
  slug: z.string(),
  title: z.string(),
  board_id: z.string().default(''),
  column_id: z.string(),
  // Имя этапа (заказчик колонок доски не знает — ему приходит готовое имя).
  stage_name: z.string(),
  // Срок текущего шага (employee-only).
  stage_deadline: z.string().optional(),
  outcome: ProjectOutcomeSchema.optional(),
  responsible_id: z.string().optional(),
  responsible_name: z.string().optional(),
  amount_kopecks: z.number().optional(),
  priority: z.number().int().min(1).max(10).optional(),
  tags: z.array(z.string()).optional(),
  // Число открытых задач по проекту (employee-only, для таблицы проектов компании).
  open_task_count: z.number().optional(),
  // Открытые блокирующие задачи (employee-only, omitempty).
  open_blocking_task_count: z.number().optional(),
  // Проект убран в архив: из списков он ушёл, показывается только с тумблером.
  archived: z.boolean().default(false),
})
export type ProjectSummary = z.infer<typeof ProjectSummarySchema>

export const ProjectTeamMemberSchema = z.object({ id: z.string(), name: z.string() })

// Этап воронки в customer-срезе проекта: имя + положение относительно текущего.
export const ProjectStageSchema = z.object({
  id: z.string(),
  name: z.string(),
  state: z.enum(['done', 'current', 'upcoming']),
})
export type ProjectStage = z.infer<typeof ProjectStageSchema>

// Полный проект (read). База — общая; employee-only поля опциональны,
// customer-only срез (счётчики, stages) — тоже.
export const ProjectReadSchema = z.object({
  id: z.string(),
  company_id: z.string(),
  company_name: z.string(),
  company_slug: z.string(),
  slug: z.string(),
  title: z.string(),
  board_id: z.string().default(''),
  column_id: z.string(),
  // Проект убран в архив: карточка открывается, но из работы он выведен.
  archived: z.boolean().default(false),
  stage_log: z.array(StageEntrySchema),
  outcome: ProjectOutcomeSchema.optional(),
  version: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  responsible_id: z.string().optional(),
  responsible_name: z.string().optional(),
  team: z.array(ProjectTeamMemberSchema).optional(),
  amount_kopecks: z.number().optional(),
  start_date: z.string().optional(),
  // Срок текущего шага — employee-only: заказчику внутренний ориентир не виден.
  stage_deadline: z.string().optional(),
  links: z.array(ProjectLinkSchema).optional(),
  priority: z.number().int().min(1).max(10).optional(),
  tags: z.array(z.string()).optional(),
  // Открытые блокирующие задачи (employee-only, omitempty).
  open_blocking_task_count: z.number().optional(),
  // Customer-срез (только заказчику): менеджер, счётчики, воронка.
  document_count: z.number().optional(),
  open_task_count: z.number().optional(),
  invoice_count: z.number().optional(),
  stages: z.array(ProjectStageSchema).optional(),
})
export type ProjectRead = z.infer<typeof ProjectReadSchema>
